import type {
  AdminFeedbackRequest,
  AdminFeedbackResponse,
  AdminSessionResponse,
  CreateFeedbackRequestBody,
  CreatedFeedbackRequest,
  DashboardSummary,
  ListFeedbackRequestsResponse,
  ListFeedbackResponsesResponse,
  PhotoReadUrlResponse,
  UpdateResponseBody,
} from '@fois-utsavvoice/shared';
import { apiRequest } from './client.js';

export function getAdminSession(accessToken: string): Promise<AdminSessionResponse> {
  return apiRequest<AdminSessionResponse>('/v1/admin/session', { accessToken });
}

export function createFeedbackRequest(
  accessToken: string,
  body: CreateFeedbackRequestBody,
): Promise<CreatedFeedbackRequest> {
  return apiRequest<CreatedFeedbackRequest>('/v1/admin/feedback-requests', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

function querySuffix(query: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) {
      params.set(key, value);
    }
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export function listFeedbackRequests(
  accessToken: string,
  query: { status?: string; cursor?: string } = {},
): Promise<ListFeedbackRequestsResponse> {
  return apiRequest<ListFeedbackRequestsResponse>(
    `/v1/admin/feedback-requests${querySuffix(query)}`,
    { accessToken },
  );
}

export function getFeedbackRequest(
  accessToken: string,
  requestId: string,
): Promise<AdminFeedbackRequest> {
  return apiRequest<AdminFeedbackRequest>(`/v1/admin/feedback-requests/${requestId}`, {
    accessToken,
  });
}

export function disableFeedbackRequest(
  accessToken: string,
  requestId: string,
): Promise<AdminFeedbackRequest> {
  return apiRequest<AdminFeedbackRequest>(`/v1/admin/feedback-requests/${requestId}/disable`, {
    method: 'POST',
    accessToken,
  });
}

export function getDashboardSummary(accessToken: string): Promise<DashboardSummary> {
  return apiRequest<DashboardSummary>('/v1/admin/dashboard/summary', { accessToken });
}

export function listResponses(
  accessToken: string,
  query: Record<string, string | undefined> = {},
): Promise<ListFeedbackResponsesResponse> {
  return apiRequest<ListFeedbackResponsesResponse>(`/v1/admin/responses${querySuffix(query)}`, {
    accessToken,
  });
}

export function getResponse(
  accessToken: string,
  responseId: string,
): Promise<AdminFeedbackResponse> {
  return apiRequest<AdminFeedbackResponse>(`/v1/admin/responses/${responseId}`, { accessToken });
}

export function updateResponse(
  accessToken: string,
  responseId: string,
  body: UpdateResponseBody,
): Promise<AdminFeedbackResponse> {
  return apiRequest<AdminFeedbackResponse>(`/v1/admin/responses/${responseId}`, {
    method: 'PATCH',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function getResponsePhotoUrl(
  accessToken: string,
  responseId: string,
): Promise<PhotoReadUrlResponse> {
  return apiRequest<PhotoReadUrlResponse>(`/v1/admin/responses/${responseId}/photo-url`, {
    accessToken,
  });
}

export async function downloadResponsesCsv(
  accessToken: string,
  query: Record<string, string | undefined> = {},
): Promise<void> {
  const response = await fetch(`/v1/admin/exports/responses.csv${querySuffix(query)}`, {
    headers: { authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    throw new Error('EXPORT_FAILED');
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = 'fois-utsavvoice-responses.csv';
  anchor.click();
  URL.revokeObjectURL(href);
}
